import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";

import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { resetALL, catalogLoad } from "Redux/actions";

const INITIAL_STATE = {
  openModal: false,
  loading: false,
  chats: [],
  total: 0,
  offset: 0,
  error: null,
  isExpanded: true,
  isChatsLoading: false,
};

const route = "modules/chat/views/chatsContainer/redux/slice";

export const openChatList = createAsyncThunk(
  "chat/list/open",
  async ({ variableQuery }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(catalogLoad(["chatUsers"]));
      dispatch(fetchChats({ offset: 0, variableQuery }));
      return;
    } catch (error) {
      ExceptionManager(error, route, "chatListOpen");
      return rejectWithValue(error);
    }
  }
);

export const fetchChats = createAsyncThunk(
  "chat/list/search",
  async ({ offset, conditions, variableQuery }, { rejectWithValue }) => {
    try {
      const result = await clientQuery(
        `
        query searchChatChannels ($conditions: [ChatChannelSearchInput], $variableQuery: ConditionChannel, $sort: [ChatChannelSortInput], $limit: Int, $offset: Int) {
          data: searchChatChannels (conditions: $conditions, variableQuery: $variableQuery, sort: $sort, limit: $limit, offset: $offset) {
            items {
              id
              isGroup
              name
              lastMessage {
                id
                message
                createdOn
                status
                transmitterEntityNameId
                receiverEntityNameId
              }
              users
              avatar
              createdOn
              createdBy
              updatedOn
              updatedBy
            }
            total
          }
        }
      `,
        {
          limit: 10,
          offset,
          conditions,
          variableQuery,
          sort: [{ field: "lastMessageDate", order: "DESC" }],
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return result?.data || { items: [], total: 0 };
    } catch (error) {
      ExceptionManager(error, route, "chatListSearch");
      return rejectWithValue(error);
    }
  }
);

const chatsContainerRedux = createSlice({
  name: "chat/list",
  initialState: INITIAL_STATE,
  reducers: {
    reset(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INITIAL_STATE };
    },
    chatListUpdateLastMessage(state, action) {
      const nextChats = _.clone(state.chats);
      const index = nextChats.findIndex((chat) => chat.id === action.payload.chatId);
      if (index > -1) {
        // Normalize createdOn to ISO string to prevent moment.js deprecation warnings
        const normalizedLastMessage = {
          ...action.payload.lastMessage,
          createdOn: action.payload.lastMessage.createdOn instanceof Date
            ? action.payload.lastMessage.createdOn.toISOString()
            : action.payload.lastMessage.createdOn
        };
        nextChats[index] = { ...nextChats[index], lastMessage: normalizedLastMessage };
      }

      state.chats = nextChats;
    },
    close(state) {
      state.openModal = false;
    },
    setIsExpanded(state, action) {
      state.isExpanded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INITIAL_STATE);

    builder.addCase(openChatList.pending, (state) => {
      state.loading = true;
      state.chats = [];
      state.total = 0;
      state.offset = 0;
      state.isChatsLoading = true;
    });
    builder.addCase(openChatList.fulfilled, (state) => {
      state.loading = false;
      state.openModal = true;
    });
    builder.addCase(openChatList.rejected, (state, action) => {
      state.loading = false;
      state.isChatsLoading = false;
      state.error = action.payload;
    });

    builder.addCase(fetchChats.pending, (state) => {
      state.loading = true;
      state.isChatsLoading = true;
    });
    builder.addCase(fetchChats.fulfilled, (state, action) => {
      const { total, items } = action.payload;
      
      // Normalize createdOn fields in lastMessage to prevent moment.js deprecation warnings
      const normalizedItems = (items || []).map(chat => ({
        ...chat,
        lastMessage: chat.lastMessage ? {
          ...chat.lastMessage,
          createdOn: chat.lastMessage.createdOn instanceof Date
            ? chat.lastMessage.createdOn.toISOString()
            : chat.lastMessage.createdOn
        } : chat.lastMessage
      }));
      
      const existingChats = _.clone(state.chats);
      state.loading = false;
      state.isChatsLoading = false;
      state.offset = action.meta.arg.offset;
      state.chats = action.meta.arg.offset === 0 ? normalizedItems : [...existingChats, ...normalizedItems];
      state.total = total;
    });
    builder.addCase(fetchChats.rejected, (state, action) => {
      state.loading = false;
      state.isChatsLoading = false;
      state.error = action.payload;
    });
  },
});

export const { actions, reducer } = chatsContainerRedux;
export const { reset, close, chatListUpdateLastMessage, setIsExpanded } = actions;
export default reducer;
